/**
 * Action: create_request
 *
 * Insert a row into the canonical public.requests queue from a
 * Solomon-captured freeform item. This is the routed sibling of
 * create_task: when capture classification says "this is really a
 * prayer/care/facility/admin request", the planner proposes this
 * action instead of a generic follow-up task.
 */

import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { logAudit } from "@/lib/audit";
import {
  FORGE_CAPTURE_CATEGORIES,
  coerceForgeCaptureCategory,
} from "@/lib/forge-capture-classification";
import { notifyRequestCreated } from "@/lib/request-notifications";
import { toDbRequestCategory } from "@/lib/request-category-compat";
import { registerAction } from "../registry";
import type { ActionDefinition, ActionResult } from "../types";

const requestCategories = ["care", "facility", "admin", "other"] as const;
const careTypes = [
  "hospital_visit",
  "counseling",
  "follow_up",
  "prayer",
  "other",
  "visit",
  "meal",
  "benevolence",
] as const;
const requestPriorities = ["low", "normal", "high"] as const;
const requestVisibilities = [
  "normal",
  "private",
  "restricted",
  "clergy_only",
  "ministry_team",
  "prayer_list",
] as const;
const requestChannels = ["member_portal", "staff", "kiosk", "import", "api"] as const;

const createRequestParams = z.object({
  summary: z.string().trim().min(1).max(500),
  details: z.string().trim().max(5000).optional().nullable(),
  category: z.enum(requestCategories).default("other"),
  care_type: z.enum(careTypes).optional().nullable(),
  priority: z.enum(requestPriorities).default("normal"),
  visibility: z.enum(requestVisibilities).default("normal"),
  channel: z.enum(requestChannels).default("staff"),
  due_at: z.string().min(1).optional().nullable(),
  person_id: z.string().guid().optional().nullable(),
  requester_person_id: z.string().guid().optional().nullable(),
  add_to_prayer_list: z.boolean().default(false),
  capture_category: z
    .preprocess(
      coerceForgeCaptureCategory,
      z.enum(FORGE_CAPTURE_CATEGORIES),
    )
    .optional(),
});

type CreateRequestParams = z.infer<typeof createRequestParams>;
type RequestInsert = Database["public"]["Tables"]["requests"]["Insert"];

async function resolvePersonName(
  organizationId: string,
  personId: string | null | undefined,
): Promise<string | null> {
  if (!personId) return null;
  const { data, error } = await supabase
    .from("people")
    .select("first_name, last_name")
    .eq("organization_id", organizationId)
    .eq("id", personId)
    .maybeSingle();
  if (error || !data) return null;
  const name = [data.first_name ?? "", data.last_name ?? ""]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" ");
  return name || null;
}

function labelForCategory(params: CreateRequestParams): string {
  if (params.care_type === "prayer") return "prayer request";
  switch (params.category) {
    case "care":
      return "care request";
    case "facility":
      return "facility request";
    case "admin":
      return "admin request";
    case "other":
      return "request";
  }
}

function isPrayerListRequest(params: CreateRequestParams): boolean {
  return params.visibility === "prayer_list" || params.add_to_prayer_list === true;
}

const createRequestDefinition: ActionDefinition<CreateRequestParams> = {
  id: "create_request",
  label: "Create request",
  description:
    "Create a care, prayer, facility, admin, or general request in the current organization.",
  requiredTier: "staff",
  paramsSchema: createRequestParams,
  defaultRequiresConfirm: true,
  async buildPreview(params, ctx) {
    const personName = await resolvePersonName(ctx.organizationId, params.person_id);
    const parts: string[] = [`Create ${labelForCategory(params)} "${params.summary}"`];
    if (personName) parts.push(`for ${personName}`);
    if (params.due_at) parts.push(`due ${params.due_at}`);
    if (isPrayerListRequest(params)) {
      parts.push("and publish it to the member prayer list");
    }
    return { summary: parts.join(" ") };
  },
  async execute(params, ctx): Promise<ActionResult> {
    const personName = await resolvePersonName(ctx.organizationId, params.person_id);
    const dbCategory = toDbRequestCategory(params.category, {
      careType: params.care_type ?? null,
    });

    const insert: RequestInsert = {
      organization_id: ctx.organizationId,
      summary: params.summary,
      details: params.details ?? null,
      category: dbCategory,
      care_type: params.care_type ?? null,
      priority: params.priority,
      urgency: params.priority,
      visibility: params.visibility,
      channel: params.channel,
      status: "open",
      source: "solomon_capture",
      source_metadata: {
        action_source: ctx.source,
        capture_category: params.capture_category ?? null,
        reason: ctx.reason ?? null,
        routed_from: "freeform_capture",
      },
      due_at: params.due_at ?? null,
      person_id: params.person_id ?? null,
      requester_person_id: params.requester_person_id ?? null,
      add_to_prayer_list:
        params.add_to_prayer_list || params.visibility === "prayer_list",
    };

    const { data, error } = await supabase
      .from("requests")
      .insert(insert)
      .select("id")
      .single();

    if (error || !data) {
      return {
        ok: false,
        summary: "Failed to create request",
        error: error?.message ?? "Unknown insert failure",
      };
    }

    const audit = await logAudit({
      action: "request.create",
      tableName: "requests",
      recordId: data.id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      newData: {
        summary: params.summary,
        category: params.category,
        db_category: dbCategory,
        care_type: params.care_type ?? null,
        person_id: params.person_id ?? null,
        person_name: personName,
        priority: params.priority,
        visibility: params.visibility,
        channel: params.channel,
        capture_category: params.capture_category ?? null,
        source: ctx.source,
        reason: ctx.reason,
      },
    });

    void notifyRequestCreated({
      organizationId: ctx.organizationId,
      requestId: data.id,
      summary: params.summary,
      category: dbCategory,
      triageOwnerPersonId: null,
      creatorUserId: ctx.userId,
    });

    const summary = personName
      ? `Created ${labelForCategory(params)} "${params.summary}" for ${personName}`
      : `Created ${labelForCategory(params)} "${params.summary}"`;

    return {
      ok: true,
      summary,
      auditLogId: audit?.id ?? null,
      inverse: null,
    };
  },
};

registerAction(createRequestDefinition);

export { createRequestDefinition };
